'use client';

import { AnimatePresence, LazyMotion, useDragControls } from 'motion/react';
import * as m from 'motion/react-m';
import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  use,
  useMemo,
  useRef,
  useState,
  type FormHTMLAttributes,
} from 'react';

import { Modal, ModalContext, ModalVariantProps, modalRecipe } from '@flipster/react/design-system/modal';
import DraggableIcon from '@flipster/react/icon/generated/DraggableIcon';
import XMarkIcon from '@flipster/react/icon/generated/XMarkIcon';
import { Portal } from '@flipster/react/misc/portal';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useCloseOnRouteChange, useSingleMovableModal } from './state';
import {
  MovableContext,
  constraintsStyle,
  getMovableDialogMotionProps,
  headerDragStyle,
  loadDomMax,
  useDragHandlePointerDown,
} from './utils';

interface RootProps {
  isOpen: boolean;
  onExit?: () => void;
  size?: ModalVariantProps['size'];
  mode?: ModalVariantProps['mode'];
  form?: ReactElement<FormHTMLAttributes<HTMLFormElement>>;
  children: ReactNode;
  onClose?: () => void;
}

const Root = (props: RootProps) => {
  const { isMobile } = useBreakpoint();
  useSingleMovableModal(props.isOpen, props.onClose);
  useCloseOnRouteChange(props.onClose);

  if (isMobile) {
    return (
      <Modal.Root
        isOpen={props.isOpen}
        onClose={props.onClose}
        onExit={props.onExit}
        size={props.size}
        form={props.form}
        mode={props.mode}
      >
        {props.children}
      </Modal.Root>
    );
  }

  return (
    <Portal selector='#modal'>
      <LazyMotion features={loadDomMax} strict>
        <DesktopLayout {...props} />
      </LazyMotion>
    </Portal>
  );
};

const DesktopLayout = ({ size, form, children, onClose, isOpen, onExit }: RootProps) => {
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const [footer, setFooter] = useState<ReactNode>(null);

  const withFooter = !!footer;
  const slots = useMemo(() => modalRecipe.raw({ size, mode: 'fullModal', withFooter }), [size, withFooter]);

  const modalCtxValue = useMemo(() => ({ slots, setFooter }), [slots]);
  const movableCtxValue = useMemo(() => ({ dragControls }), [dragControls]);
  const handleCloseAreaPointerDown = useDragHandlePointerDown(dragControls);

  return (
    <ModalContext value={modalCtxValue}>
      <MovableContext value={movableCtxValue}>
        <AnimatePresence onExitComplete={onExit}>
          {isOpen && (
            <div ref={constraintsRef} className={css(constraintsStyle)}>
              <m.aside
                key='movable'
                className={css(slots.dialog, dialogOverride)}
                {...getMovableDialogMotionProps(dragControls, constraintsRef)}
              >
                <div
                  className={css(slots.closeButtonWrapper, headerDragStyle, { justifyContent: 'space-between' })}
                  onPointerDown={handleCloseAreaPointerDown}
                >
                  <DraggableIcon size={24} color={token('colors.content.default.level-2')} />
                  <button type='button' className={css(slots.closeButton)} onClick={onClose}>
                    <XMarkIcon />
                  </button>
                </div>
                <FormOrChildren form={form}>
                  <div className={css(slots.scrollWrapper)}>
                    <div className={css(slots.scrollContent)}>{children}</div>
                  </div>
                  {footer}
                </FormOrChildren>
              </m.aside>
            </div>
          )}
        </AnimatePresence>
      </MovableContext>
    </ModalContext>
  );
};

const dialogOverride = {
  pointerEvents: 'auto',
} as const;

const Header = ({ children }: PropsWithChildren) => {
  const movable = use(MovableContext);
  const modalCtx = use(ModalContext);
  const handlePointerDown = useDragHandlePointerDown(movable?.dragControls);

  if (!movable || !modalCtx) return <Modal.Header>{children}</Modal.Header>;

  return (
    <header className={css(modalCtx.slots.header, headerDragStyle)} onPointerDown={handlePointerDown}>
      {children}
    </header>
  );
};

const Title = Modal.Title;
const Subtitle = Modal.Subtitle;
const Content = Modal.Content;
const Footer = Modal.Footer;
const Button = Modal.Button;

Root.displayName = 'MovableModal.Root';
Header.displayName = 'MovableModal.Header';

export { Root, Header, Title, Subtitle, Content, Footer, Button };

const FormOrChildren = ({ form, children }: PropsWithChildren<{ form: RootProps['form'] }>) => {
  if (form && isValidElement(form) && form.type === 'form')
    return cloneElement(form, { children, className: stack({ w: '100%', flex: 1 }) });
  return children;
};
